import { useEffect, useRef, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SiteLayout, PageHero, ReferBtn, Caps } from "../components/site";
import { CONTACT } from "../content";
import { EnquiryForm } from "../components/enquiry";
import { TEAM } from "../team";

const STEVEN = TEAM.find((m) => m.id === "steven");

const ORIGIN = "https://inner-you-development-pty-ltd.splose.com";
const MODALITIES = [
  { id: "psychology-adults", label: "Psychology 14+ and Adults", uuid: "0652b05f-d854-4f39-9b6d-ee4062e31b0c" },
  { id: "psychology-all-ages", label: "Psychology Children", uuid: "1f1305b3-05a5-4ca3-8209-7c0520800bfd" },
  { id: "ot", label: "Occupational Therapy", options: [
    { id: "occupational-therapy", label: "Occupational Therapy", uuid: "3a81d6d0-77e0-4e44-bb8b-de96a81d65bc" },
    { id: "mental-health-ot", label: "Mental Health Occupational Therapy", uuid: "81bb605c-af0f-4fcf-90e5-1a6427b7bf9c" },
  ] },
  { id: "parenting", label: "Parenting Programs", eoi: true },
  { id: "dietitian", label: "Dietician", uuid: "b13fad7f-9a71-4a4c-8205-a852e4b08d67" },
  { id: "speech-therapy", label: "Speech Therapy", uuid: "cb0efe62-0444-4d96-bf67-79962502d879" },
  { id: "social-work", label: "Social Worker", uuid: "8c021d06-6460-4528-a473-496c310f9a5e" },
  { id: "counselling", label: "Counselling", uuid: "91d78f9c-2818-4ab1-822e-169db331f273" },
];
export const BOOKABLE = MODALITIES.flatMap((m) => m.options || [m]);

const ParentingEoi = () => {
  const [sent, setSent] = useState(false);
  return (
    <div className="px-3 sm:px-6 pb-6" data-testid="parenting-eoi">
      <h3 className="mt-2 font-newsreader text-2xl sm:text-3xl text-[#1F4D3A]">Register your interest</h3>
      <p className="mt-2 mb-6 max-w-2xl font-jakarta text-sm text-[#4A4F45]">Tell us which program you're interested in and we'll be in touch with upcoming dates and next steps.</p>
      {sent ? (
        <p className="rounded-2xl bg-[#E3E9E4] px-5 py-4 font-jakarta text-sm text-[#3F4A3C]" data-testid="parenting-eoi-sent">Thank you. We've received your expression of interest and will be in touch soon.</p>
      ) : (
        <EnquiryForm topic="Parenting programs – expression of interest" initialMessage="I'd like to register my interest in: " onSent={() => setSent(true)} />
      )}
    </div>
  );
};

const chip = (on) => `group flex items-center justify-between gap-3 rounded-2xl border px-5 py-4 text-left font-jakarta text-sm sm:text-[15px] transition-[background-color,border-color,color] duration-300 ${on ? "bg-[#1F4D3A] border-[#1F4D3A] text-[#F6F2E9]" : "bg-white border-[#3F4A3C]/15 text-[#3F4A3C] hover:border-[#C59B45] hover:bg-[#FBF9F4]"}`;

const Chip = ({ m, on, onClick, open }) => (
  <button type="button" onClick={onClick} aria-pressed={on} aria-expanded={open} data-testid={`book-modality-${m.id}`} className={chip(on)}>
    {m.label}<ArrowRight className={`w-4 h-4 shrink-0 transition-transform duration-300 ${open ? "rotate-90" : "group-hover:translate-x-0.5"} ${on ? "text-[#F3DCA8]" : "text-[#C59B45]"}`} />
  </button>
);

const ModalityPicker = ({ active, onPick }) => {
  const [openId, setOpenId] = useState(() => MODALITIES.find((m) => m.options?.some((o) => o.id === active))?.id || null);
  return (
    <div className="grid sm:grid-cols-2 grid-flow-row-dense gap-3" data-testid="book-modalities">
      {MODALITIES.map((m) => {
        if (!m.options) return <Chip key={m.id} m={m} on={m.id === active} onClick={() => { setOpenId(null); onPick(m.id); }} />;
        const open = openId === m.id;
        return (
          <Fragment key={m.id}>
            <Chip m={m} on={open} open={open} onClick={() => setOpenId(open ? null : m.id)} />
            {open && (
              <div className="sm:col-span-2 rounded-2xl border border-[#C59B45]/40 bg-[#FBF9F4] p-2 grid sm:grid-cols-2 gap-2" data-testid={`book-suboptions-${m.id}`}>
                {m.options.map((o) => <Chip key={o.id} m={o} on={o.id === active} onClick={() => onPick(o.id)} />)}
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export default function Book() {
  const ref = useRef(null);
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(() => (BOOKABLE.some((m) => m.id === params.get("type")) ? params.get("type") : null));
  const current = BOOKABLE.find((m) => m.id === active);
  const pick = (id) => {
    setActive(id);
    setParams({ type: id }, { replace: true });
    setTimeout(() => { document.getElementById("booking-calendar")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 80);
  };
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => { document.getElementById("booking-calendar")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 400);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onMsg = (e) => {
      const f = ref.current;
      if (e.origin !== ORIGIN || !e.data?.type || !f || e.source !== f.contentWindow) return;
      if (e.data.type === "sploseIframeResize" && Number.isFinite(e.data.height) && e.data.height >= 0) f.style.height = `${e.data.height}px`;
      if (e.data.type === "sploseIframeScrollToTop") f.scrollIntoView({ behavior: "smooth", block: "start" });
      if (e.data.type === "sploseBookingConfirmed") {
        const p = e.data.payload || {};
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "splose_booking_confirmed", booking_page_uuid: String(p.booking_page_uuid), booking_page_name: String(p.booking_page_name), service_name: String(p.service_name), location_name: String(p.location_name), service_price: Number(p.service_price) });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  return (
    <SiteLayout testid="book-page">
      <PageHero eyebrow="Book online" lines={["Choose a time", "that suits you."]} sub={`Not sure what to book? Call ${CONTACT.phone}.`}>
        <ReferBtn testid="book-refer" />
      </PageHero>
      <section className="max-w-[1100px] mx-auto px-3 sm:px-10 pb-24">
        <div className="mb-5 mx-2 sm:mx-0 flex items-start gap-3 rounded-[20px] bg-[#F3DCA8]/60 px-5 py-4 font-jakarta text-sm leading-relaxed text-[#3F4A3C]" data-testid="book-capacity-notice">
          <span className="mt-1.5 w-2 h-2 shrink-0 rounded-full bg-[#C59B45]" />
          <div className="space-y-2">{STEVEN.capacity.map((t, i) => <p key={i} className={i === 0 ? "font-medium" : ""}>{t}</p>)}</div>
        </div>
        <Caps>Step 1 · Choose a type of appointment</Caps>
        <div id="book-types" className="scroll-mt-28 mt-5 mb-10"><ModalityPicker active={active} onPick={pick} /></div>
        <div id="booking-calendar" className="scroll-mt-28 rounded-[28px] bg-white border border-[#3F4A3C]/10 overflow-hidden p-2 sm:p-4">
          {current ? (
            <>
              <p className="px-3 pt-3 pb-2 font-jakarta text-xs uppercase tracking-[0.2em] text-[#8A6A2E]" data-testid="book-selected-modality">Step 2 · {current.label}</p>
              {current.eoi ? <ParentingEoi /> : (
              <iframe key={current.uuid} ref={ref} id={`splose-booking-${current.uuid}`} src={`${ORIGIN}/online-booking/${current.uuid}?embedVersion=v2`} title={`Online booking – ${current.label}`} name="onlineBookingEmbeded" scrolling="auto" width="100%" height="700" frameBorder="0" style={{ display: "block", transition: "height 0.3s ease" }} data-testid="splose-booking-iframe" />
              )}
            </>
          ) : (
            <p className="px-5 py-14 text-center font-jakarta text-sm text-[#4A4F45]" data-testid="book-choose-prompt">Choose a type of appointment above to see available times.</p>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
